import { compose } from "@insightfulscience/shared-utils/fn";

/*
	This is a Contentful response sanitizer removing all known and unneeded
	properties from responses that may cause issues with circular references.

	In the future we can tackle this issue with:
	a) instead of using direct response we should map it to what we need.
	b) creating custom resolver.
	c) use GQL CTF API.
*/

// General nullifier for fields on pages that can cause circular references
const nullifyFields = fields => ({
	...fields,
	body: null,
	teaser: null,
	appReleaseNotes: null,
	recommendedVideos: null,
	contentReference: null,
	additionalResources: null,
	referenceExploreMore: null,
	referenceRecommendedResources: null,
	latestBlogPosts: null,
});

// Rich text sanitizer
const sanitizeRichText = field => {
	if (!field || field.nodeType !== "document") return field;

	return {
		...field,
		content: field.content.map(el => ({
			...el,
			content: el.content.map(subEl => {
				if (subEl.nodeType !== "entry-hyperlink") return subEl;

				return {
					...subEl,
					data: {
						...subEl.data,
						target: {
							...subEl.data.target,
							fields: {
								slug: subEl.data.target.fields.slug,
							},
						},
					},
				};
			}),
		})),
	};
};

// Block sanitizers
const sanitizeBlockFeaturedContent = el => {
	if (!el || el.sys.contentType?.sys.id !== "blockFeaturedContent") return el;

	return {
		...el,
		fields: {
			...el.fields,
			...(el.fields.contentReference
				? {
						contentReference: el.fields.contentReference?.map(subEl => ({
							...subEl,
							fields: nullifyFields(subEl.fields),
						})),
				  }
				: {}),
		},
	};
};

const sanitizeBlockLinks = el => {
	if (!el || el.sys.contentType?.sys.id !== "blockLinks") return el;

	return {
		...el,
		fields: {
			...el.fields,
			...(el.fields.linkReference
				? {
						linkReference: el.fields.linkReference?.map(subEl => ({
							...subEl,
							fields: nullifyFields(subEl.fields),
						})),
				  }
				: {}),
		},
	};
};

const sanitizeBlockVisualCopy = el => {
	if (!el || el.sys.contentType?.sys.id !== "blockVisualCopy") return el;

	return {
		...el,
		fields: {
			...el.fields,
			...(el.fields.lead ? { lead: sanitizeRichText(el.fields.lead) } : {}),
		},
	};
};

const sanitizeSingle = (object, type) => {
	const newObject = { ...object };

	if (["series", "guides", "plugins"].includes(type)) {
		if ("recommendedVideos" in newObject) {
			newObject.recommendedVideos = newObject.recommendedVideos.map(rv => ({
				...rv,
				fields: nullifyFields(rv.fields),
			}));
		}

		if ("additionalResources" in newObject) {
			newObject.additionalResources = newObject.additionalResources.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		}

		if ("body" in newObject) {
			newObject.body = sanitizeRichText(newObject.body);
		}
	}

	if (type === "standard") {
		if ("contentReference" in newObject) {
			newObject.contentReference = newObject.contentReference.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		}
	}

	if (["product", "solution", "integration"].includes(type)) {
		if ("referenceExploreMore" in newObject && Array.isArray(newObject.referenceExploreMore)) {
			newObject.referenceExploreMore = newObject.referenceExploreMore.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		} else if ("referenceExploreMore" in newObject) {
			newObject.referenceExploreMore = compose(
				sanitizeBlockFeaturedContent,
				sanitizeBlockLinks,
				sanitizeBlockVisualCopy,
			)(newObject.referenceExploreMore);
		}
	}

	if (type === "releaseNotes") {
		if ("body" in newObject) {
			newObject.body = sanitizeRichText(newObject.body);
		}

		if ("teaser" in newObject) {
			newObject.teaser = sanitizeRichText(newObject.teaser);
		}

		if ("appReleaseNotes" in newObject) {
			newObject.appReleaseNotes = sanitizeRichText(newObject.appReleaseNotes);
		}
	}

	if (type === "events") {
		if ("body" in newObject) {
			newObject.body = sanitizeRichText(newObject.body);
		}
		if ("referenceBlocks" in newObject && Array.isArray(newObject.referenceBlocks)) {
			newObject.referenceBlocks = newObject.referenceBlocks.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		}
	}

	if (type === "webinar") {
		if ("body" in newObject) {
			newObject.body = sanitizeRichText(newObject.body);
		}
		if ("referenceBlocks" in newObject && Array.isArray(newObject.referenceBlocks)) {
			newObject.referenceBlocks = newObject.referenceBlocks.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		}
	}

	if (type === "institute") {
		if ("referenceRecommendedResources" in newObject) {
			newObject.referenceRecommendedResources = compose(
				sanitizeBlockFeaturedContent,
				sanitizeBlockLinks,
				sanitizeBlockVisualCopy,
			)(newObject.referenceRecommendedResources);
		}
	}

	if (type === "blog") {
		if ("body" in newObject) {
			newObject.body = sanitizeRichText(newObject.body);
		}

		if ("latestBlogPosts" in newObject) {
			newObject.latestBlogPosts = newObject.latestBlogPosts.map(
				compose(sanitizeBlockFeaturedContent, sanitizeBlockLinks, sanitizeBlockVisualCopy),
			);
		}
	}

	return newObject;
};

/**
 * @param {object} object
 * @param {'series' | 'guides' | 'plugins' | 'standard' | 'product' | 'solution' | 'integration' | 'releaseNotes' | 'webinar' | 'institute' | 'blog'} type
 * @returns {object | object[]}
 */
export const sanitize = (object, type) => {
	if (!object) return object;

	return Array.isArray(object)
		? object.map(obj => sanitizeSingle(obj, type))
		: sanitizeSingle(object, type);
};
